import styled, { css, keyframes } from 'styled-components'
import {
  SPACING,
  TYPOGRAPHY,
  COLORS,
  BORDER_RADIUS,
  TRANSITIONS,
  FONT_WEIGHT,
} from '../../../../styles/theme'

// ─── Layout ──────────────────────────────────────────────────────────────────

export const InspectorBody = styled.div`
  height: 70vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

export const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${SPACING.lg} 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border-dark);
    border-radius: 3px;
  }
`

// ─── Dashboard Stats ─────────────────────────────────────────────────────────

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${SPACING.md};
  margin-bottom: ${SPACING.xl};
`

export const StatCard = styled.div<{ $accent?: string }>`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  padding: ${SPACING.lg};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};

  ${({ $accent }) =>
    $accent &&
    css`
      border-left: 3px solid ${$accent};
    `}
`

export const StatValue = styled.div`
  font-size: ${TYPOGRAPHY.h3.fontSize};
  font-weight: ${FONT_WEIGHT.bold};
  color: var(--color-text-primary);
  line-height: 1;
  font-variant-numeric: tabular-nums;
`

export const StatLabel = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const StatusBadge = styled.span<{ $status: 'ready' | 'processing' | 'error' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: ${BORDER_RADIUS.full};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.3px;

  ${({ $status }) => {
    switch ($status) {
      case 'ready':
        return css`
          background: rgba(34, 197, 94, 0.12);
          color: #16a34a;
        `
      case 'processing':
        return css`
          background: rgba(234, 179, 8, 0.12);
          color: #ca8a04;
        `
      case 'error':
        return css`
          background: rgba(239, 68, 68, 0.12);
          color: #dc2626;
        `
    }
  }}
`

// ─── Venture Node Table ──────────────────────────────────────────────────────

export const VentureTable = styled.div`
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  overflow: hidden;
`

export const VentureTableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: ${SPACING.sm} ${SPACING.lg};
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const VentureTableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: ${SPACING.sm} ${SPACING.lg};
  border-bottom: 1px solid var(--color-border);
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: var(--color-text-primary);
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`

// ─── Documents & Chunks Tab ──────────────────────────────────────────────────

export const SplitPane = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1px;
  background: var(--color-border);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  overflow: hidden;
  height: calc(70vh - 120px);
`

export const DocListPane = styled.div`
  background: var(--color-bg-primary);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border-dark);
    border-radius: 3px;
  }
`

export const DocGroupHeader = styled.div`
  padding: ${SPACING.sm} ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border);
  position: sticky;
  top: 0;
  z-index: 1;
`

export const DocRow = styled.div<{ $active?: boolean }>`
  padding: ${SPACING.sm} ${SPACING.md};
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${({ $active }) =>
    $active
      ? css`
          background: var(--color-selected-bg);
          border-left: 3px solid ${COLORS.primary};
          padding-left: calc(${SPACING.md} - 3px);
        `
      : css`
          &:hover {
            background: var(--color-bg-hover);
          }
        `}
`

export const DocFileName = styled.div`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const DocMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

export const ChunkPane = styled.div`
  background: var(--color-bg-primary);
  overflow-y: auto;
  padding: ${SPACING.lg};

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border-dark);
    border-radius: 3px;
  }
`

export const ChunkCard = styled.div`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.md};
  margin-bottom: ${SPACING.md};
  overflow: hidden;
`

export const ChunkHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.sm} ${SPACING.md};
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
`

export const ChunkContent = styled.pre`
  padding: ${SPACING.md};
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.6;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-border-dark);
    border-radius: 2px;
  }
`

export const TokenBadge = styled.span`
  padding: 1px 6px;
  border-radius: ${BORDER_RADIUS.sm};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  background: rgba(110, 83, 249, 0.1);
  color: ${COLORS.primary};
  font-variant-numeric: tabular-nums;
`

export const LoadMoreButton = styled.button`
  width: 100%;
  padding: ${SPACING.sm};
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: ${TRANSITIONS.fast};

  &:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }
`

// ─── Node Sources Tab ────────────────────────────────────────────────────────

export const SourceNodeRow = styled.div`
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.md};
  margin-bottom: ${SPACING.sm};
  overflow: hidden;
`

export const SourceNodeHeader = styled.div<{ $expanded?: boolean }>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 80px;
  align-items: center;
  padding: ${SPACING.sm} ${SPACING.lg};
  cursor: pointer;
  transition: ${TRANSITIONS.fast};
  background: ${({ $expanded }) => ($expanded ? 'var(--color-selected-bg)' : 'transparent')};

  &:hover {
    background: var(--color-bg-hover);
  }
`

export const SourceExcerpts = styled.div`
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-secondary);
  padding: ${SPACING.md} ${SPACING.lg};
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm};
`

export const ExcerptBlock = styled.div`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.sm};
  padding: ${SPACING.sm} ${SPACING.md};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  line-height: 1.5;
  color: var(--color-text-primary);
`

export const ExcerptSource = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  margin-bottom: 4px;
`

// ─── Empty / Loading States ──────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${SPACING.xxxl};
  color: var(--color-text-secondary);
  gap: ${SPACING.md};
`

export const EmptyStateIcon = styled.div`
  opacity: 0.3;
`

export const EmptyStateText = styled.div`
  font-size: ${TYPOGRAPHY.body.fontSize};
  text-align: center;
  max-width: 320px;
`

export const LoadingPulse = styled.div`
  animation: ${pulse} 1.5s ease-in-out infinite;
  text-align: center;
  padding: ${SPACING.xxxl};
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.body.fontSize};
`

export const CategoryBadge = styled.span`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: ${BORDER_RADIUS.full};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: var(--color-bg-secondary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
`

export const DocSummary = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.4;
  margin-top: 2px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const SectionTitle = styled.h3`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  margin: 0 0 ${SPACING.md} 0;
`
